import type { HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-colors',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        outline: 'border-border text-foreground',
        success: 'border-transparent bg-success/15 text-success',
        warning: 'border-transparent bg-warning/20 text-warning-foreground',
        destructive: 'border-transparent bg-destructive/12 text-destructive',
        muted: 'border-transparent bg-muted text-muted-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

/**
 * Maps a domain status string to a Badge variant. Covers cash/inventory
 * request statuses, incident statuses, and task statuses so screens don't
 * each re-derive the mapping.
 */
export function statusVariant(status: string): NonNullable<BadgeProps['variant']> {
  switch (status) {
    case 'approved':
    case 'resolved':
    case 'closed':
    case 'completed':
    case 'done':
      return 'success';
    case 'submitted':
    case 'open':
    case 'in_progress':
    case 'pending':
      return 'warning';
    case 'rejected':
    case 'skipped':
    case 'critical':
      return 'destructive';
    default:
      return 'muted';
  }
}
